import { useEffect, useRef, useState } from "react";
import { BsCheckCircleFill, BsExclamationCircleFill, BsUpload } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import ModalComponent from "../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../components/form_fields/Label";
import { useVerifyGhanaCard } from "../../../../../services/miscellaneous-payment.service";
import { GHANA_CARD_PATTERN, inputClassName, toRecord } from "./helpers";

const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // (25 megabytes, the provider's limit)

type VerificationRecord = {
	transactionGuid?: string;
	identifier?: string;
	id?: string;
	verified?: boolean | string;
	surname?: string;
	forenames?: string;
};

interface GhanaCardVerificationModalProps {
	open: boolean;
	onClose: () => void;
	ghanaCardNumber: string;
	directorName: { lastName: string; otherNames: string };
	onVerified: (verificationId: string) => void;
}

const normaliseName = (name?: string) =>
	(name || "").toUpperCase().replace(/\s+/g, " ").trim();

const GhanaCardVerificationModal = ({
	open,
	onClose,
	ghanaCardNumber: initialCardNumber,
	directorName,
	onVerified,
}: GhanaCardVerificationModalProps) => {
	const [ghanaCardNumber, setGhanaCardNumber] = useState(initialCardNumber);
	// The face image is sent to the backend as a file, not uploaded to S3 first
	const [image, setImage] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState("");
	const imageInputRef = useRef<HTMLInputElement>(null);
	const [result, setResult] = useState<VerificationRecord>();
	const { mutate: verifyGhanaCard, isLoading } = useVerifyGhanaCard();

	const cardNumberValid = GHANA_CARD_PATTERN.test(ghanaCardNumber);
	const verificationId = result?.transactionGuid || result?.identifier || result?.id;
	const isVerified =
		result?.verified === true || String(result?.verified).toUpperCase() === "TRUE";
	const namesMatch =
		normaliseName(result?.surname) === normaliseName(directorName.lastName) &&
		normaliseName(result?.forenames) === normaliseName(directorName.otherNames);

	useEffect(() => {
		if (!image) {
			setImagePreview("");
			return;
		}
		const previewUrl = URL.createObjectURL(image);
		setImagePreview(previewUrl);
		return () => URL.revokeObjectURL(previewUrl);
	}, [image]);

	const selectImage = (file?: File) => {
		if (!file) return;
		if (!file.type.startsWith("image/")) {
			toast.error("Choose a JPG or PNG photo");
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			toast.error("Photo is too large. The maximum size is 25 MB");
			return;
		}
		setImage(file);
	};

	const verify = () => {
		if (!image) return;
		verifyGhanaCard(
			{ payload: { ghanaCardNumber, file: image } },
			{ onSuccess: (response) => setResult(toRecord<VerificationRecord>(response)) },
		);
	};

	return (
		<ModalComponent open={open} title="Verify Ghana Card" width={560} handleClose={onClose}>
			<div className="space-y-5">
				<p className="text-xs text-gray-600 bg-amber-50 rounded-lg px-4 py-3">
					Each verification is charged by the provider. Make sure the card number and
					photo are correct before you verify.
				</p>
				<div>
					<Label for="ghanaCardNumber" title="Ghana Card number" />
					<input
						id="ghanaCardNumber"
						value={ghanaCardNumber}
						onChange={(event) => setGhanaCardNumber(event.target.value.toUpperCase())}
						placeholder="GHA-123456789-0"
						disabled={!!result}
						className={inputClassName(!!ghanaCardNumber && !cardNumberValid)}
					/>
					{!!ghanaCardNumber && !cardNumberValid && (
						<p className="text-red-500 text-xs mt-1">Use the format GHA-123456789-0</p>
					)}
				</div>
				<div>
					<Label for="faceImage" title="Clear photo of the director's face" />
					<input
						ref={imageInputRef}
						id="faceImage"
						type="file"
						accept="image/png,image/jpeg"
						className="hidden"
						onChange={(event) => {
							selectImage(event.target.files?.[0]);
							event.target.value = "";
						}}
					/>
					{image ? (
						<div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 p-3">
							<img
								src={imagePreview}
								alt="Director"
								className="h-14 w-14 rounded object-cover shrink-0"
							/>
							<span className="text-sm text-gray-700 truncate flex-1">{image.name}</span>
							{!result && (
								<>
									<button
										type="button"
										onClick={() => imageInputRef.current?.click()}
										className="text-xs text-gray-600 hover:text-primary-blue"
									>
										Replace
									</button>
									<button type="button" onClick={() => setImage(null)} title="Remove">
										<IoMdClose className="text-lg text-gray-500" />
									</button>
								</>
							)}
						</div>
					) : (
						<button
							type="button"
							onClick={() => imageInputRef.current?.click()}
							className="mt-2 w-full h-14 rounded-lg border border-dashed border-gray-300 flex items-center justify-center gap-2 text-sm text-gray-600 hover:border-primary-blue hover:text-primary-blue"
						>
							<BsUpload />
							Choose photo (JPG or PNG)
						</button>
					)}
				</div>

				{result && (
					<div
						className={`rounded-lg px-4 py-3 text-sm ${
							isVerified ? "bg-green-50" : "bg-red-50"
						}`}
					>
						<div className="flex items-center gap-2 font-medium">
							{isVerified ? (
								<BsCheckCircleFill className="text-green-600" />
							) : (
								<BsExclamationCircleFill className="text-red-500" />
							)}
							{isVerified ? "Card verified" : "Card could not be verified"}
						</div>
						{(result.surname || result.forenames) && (
							<p className="text-xs text-gray-700 mt-2">
								Name on card:{" "}
								<span className="font-medium">
									{result.forenames} {result.surname}
								</span>
							</p>
						)}
						{isVerified && !namesMatch && (
							<p className="text-xs text-amber-700 mt-2">
								This doesn't match the name entered for the director (
								{directorName.otherNames} {directorName.lastName}). Update the
								director's name to match the card exactly, or the submission may be
								declined.
							</p>
						)}
					</div>
				)}

				<div className="flex justify-end gap-3 pt-2">
					<button
						type="button"
						onClick={onClose}
						className="h-12 px-6 border border-gray-300 rounded-lg text-sm text-gray-600"
					>
						Cancel
					</button>
					{result && isVerified && verificationId ? (
						<button
							type="button"
							onClick={() => {
								onVerified(verificationId);
								onClose();
							}}
							className="bg-primary-mango text-white h-12 px-6 rounded-lg text-sm"
						>
							Use this verification
						</button>
					) : result ? (
						<button
							type="button"
							onClick={() => setResult(undefined)}
							className="bg-primary-mango text-white h-12 px-6 rounded-lg text-sm"
						>
							Try again
						</button>
					) : (
						<button
							type="button"
							onClick={verify}
							disabled={!cardNumberValid || !image || isLoading}
							className="bg-primary-mango text-white h-12 px-6 rounded-lg text-sm disabled:opacity-50"
						>
							{isLoading ? "Verifying..." : "Verify card"}
						</button>
					)}
				</div>
			</div>
		</ModalComponent>
	);
};

export default GhanaCardVerificationModal;
